import { QueryDocumentSnapshot } from 'firebase/firestore';
import { 
  Payable,
  Receivable
} from '@/types/finance';
import { db } from '../firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc,
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  limit, 
  startAfter,
  setDoc,
  arrayUnion
} from 'firebase/firestore';
import { addWorkspaceAndActivity, isDemoWorkspace, createDetailedActivityRecord } from '@/utils/apiHelpers';
import { User } from '../types/users';
import { loadMockData } from '@/mock';
import { removeUndefined } from '@/utils/removeUndefined';
import { formatFirestoreTimestamp } from '@/utils/date';
import { Receipt } from '@/types/common';

// Helper function to filter mock data
function filterMockData<T extends { name: string; status: string; vendor?: { name: string }; resident?: { name: string } }>(
  data: T[],
  searchTerm?: string,
  status?: string
): T[] {
  let filteredData = data;
  
  if (searchTerm) {
    const lowercasedSearch = searchTerm.toLowerCase();
    filteredData = filteredData.filter(item => 
      item.name.toLowerCase().includes(lowercasedSearch) ||
      (item.vendor?.name.toLowerCase().includes(lowercasedSearch)) ||
      (item.resident?.name.toLowerCase().includes(lowercasedSearch))
    );
  }
  
  if (status) {
    filteredData = filteredData.filter(item => item.status === status);
  }
  
  // Note: type filtering is not supported for all data types
  // This is handled by the calling function if needed
  
  return filteredData;
}

// Helper function to simulate pagination for mock data
function paginateMockData<T>(
  data: T[],
  pageSize?: number,
  currentPage: number = 1
): { data: T[]; total: number } {
  if (!pageSize) {
    return { data, total: data.length };
  }
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = data.slice(startIndex, endIndex);
  return {
    data: paginatedData,
    total: data.length
  };
}

// Constants
const PAYABLES_COLLECTION = 'payables';
const RECEIVABLES_COLLECTION = 'receivables';
const RECEIPTS_COLLECTION = 'receipts';

// Types
export interface FinanceQueryParams {
  searchTerm?: string;
  pageSize?: number;
  page?: number;
  lastDoc?: QueryDocumentSnapshot;
  firestoreUser: User;
  status?: string;
  type?: string;
}

// Payables API
export async function getPayables({
  searchTerm,
  pageSize,
  page = 1,
  lastDoc,
  firestoreUser,
  status,
  type
}: FinanceQueryParams): Promise<{ payables: Payable[]; lastDoc?: QueryDocumentSnapshot; total: number }> {
  // Debug: Log user workspace info
  console.log('🔍 Debug - getPayables called with:', {
    firestoreUser: firestoreUser ? {
      id: firestoreUser.id,
      email: firestoreUser.email,
      workspace: firestoreUser.workspace
    } : null,
    isDemo: isDemoWorkspace(firestoreUser)
  });

  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    console.log('DEMO MODE: Fetching mock payables with filters', { searchTerm, status, type, page, pageSize });
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate delay
    
    // Load language-specific mock data
    const mockPayables = await loadMockData<Payable[]>('payables', firestoreUser.language);
    console.log('🔍 Debug - Mock payables loaded:', mockPayables.length);
    
    const filteredData = filterMockData(mockPayables, searchTerm, status);
    const paginatedData = paginateMockData(filteredData, pageSize, page);
    
    return {
      payables: paginatedData.data,
      lastDoc: undefined,
      total: paginatedData.total
    };
  }

  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }
  
    const payablesRef = collection(db, PAYABLES_COLLECTION);
    const constraints = [where('workspace.id', '==', firestoreUser.workspace.id)];
    
    if (searchTerm) {
      constraints.push(
        where('name', '>=', searchTerm),
        where('name', '<=', searchTerm + '\uf8ff')
      );
    }

    if (status) {
      constraints.push(where('status', '==', status));
    }

    if (type) {
      constraints.push(where('type', '==', type));
    }
    
    let q = query(payablesRef, ...constraints);
    
    if (pageSize) {
      q = query(q, limit(pageSize));
    }
    
    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }
    
    const snapshot = await getDocs(q);
    const payables = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Payable));
    
    return {
      payables,
      lastDoc: snapshot.docs[snapshot.docs.length - 1],
      total: payables.length
    };
  } catch (error) {
    console.error('Error fetching payables:', error);
    throw new Error('Failed to fetch payables');
  }
}

export async function getPayablesByMonth(
  year: number,
  month: number,
  firestoreUser: User
): Promise<Payable[]> {
  console.log('🔍 Debug - getPayablesByMonth called with:', {
    year,
    month,
    firestoreUser: firestoreUser ? {
      id: firestoreUser.id,
      email: firestoreUser.email,
      workspace: firestoreUser.workspace
    } : null,
    isDemo: isDemoWorkspace(firestoreUser)
  });

  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    console.log('DEMO MODE: Fetching mock payables for month', { year, month });
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate delay
    
    // Load language-specific mock data
    const mockPayables = await loadMockData<Payable[]>('payables', firestoreUser.language);
    console.log('🔍 Debug - Mock payables loaded:', mockPayables.length);
    
    // Filter payables for the specified month
    const filteredPayables = mockPayables.filter(payable => {
      if (!payable.dueDate) return false;
      
      const dueDate = formatFirestoreTimestamp(payable.dueDate);
      if (!dueDate) return false;
      
      const payableYear = dueDate.getFullYear();
      const payableMonth = dueDate.getMonth() + 1; // getMonth() returns 0-11, we need 1-12
      
      return payableYear === year && payableMonth === month;
    });
    
    console.log('🔍 Debug - Filtered payables for month:', filteredPayables.length);
    return filteredPayables;
  }

  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    // Get all payables for the workspace and filter by month client-side
    // This avoids the need for a composite index
    const payablesRef = collection(db, PAYABLES_COLLECTION);
    const q = query(
      payablesRef,
      where('workspace.id', '==', firestoreUser.workspace.id)
    );
    
    const snapshot = await getDocs(q);
    const allPayables = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Payable));
    
    // Filter by month client-side
    const startDate = new Date(year, month - 1, 1);
    const endDate = new Date(year, month, 0, 23, 59, 59, 999);
    
    const payables = allPayables.filter(payable => {
      if (!payable.dueDate) return false;
      
      const dueDate = formatFirestoreTimestamp(payable.dueDate);
      if (!dueDate) return false;
      
      return dueDate >= startDate && dueDate <= endDate;
    });
    
    console.log('🔍 Debug - Firestore payables for month:', payables.length);
    return payables;
  } catch (error) {
    console.error('Error fetching payables by month:', error);
    throw new Error('Failed to fetch payables for the month');
  }
}

export async function getPayableById(id: string, firestoreUser: User): Promise<Payable | null> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    console.log(`DEMO MODE: Fetching mock payable with id: ${id}`);
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate delay
    
    // Load language-specific mock data
    const mockPayables = await loadMockData<Payable[]>('payables', firestoreUser.language);
    const payable = mockPayables.find(p => p.id === id) || null;
    return payable;
  }

  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const payableRef = doc(db, PAYABLES_COLLECTION, id);
    const payableDoc = await getDoc(payableRef);
    
    if (!payableDoc.exists()) {
      return null;
    }

    const payableData = payableDoc.data();
    
    if (payableData.workspace.id !== firestoreUser.workspace.id) {
      return null;
    }

    return { ...payableData, id: payableDoc.id } as Payable;
  } catch (error) {
    console.error('Error fetching payable:', error);
    throw new Error('Failed to fetch payable');
  }
}

export async function addPayable(payable: Omit<Payable, 'id' | 'activityRecords' | 'workspace'>, firestoreUser: User): Promise<string> {
  try {
    const payableData = addWorkspaceAndActivity(payable, firestoreUser, 'created');
    const cleanedPayable = removeUndefined(payableData);
    
    const payableRef = doc(collection(db, PAYABLES_COLLECTION));
    await setDoc(payableRef, cleanedPayable);
    return payableRef.id;
  } catch (error) {
    console.error('Error adding payable:', error);
    throw new Error('Failed to add payable');
  }
}

export async function updatePayable(id: string, data: Partial<Payable>, firestoreUser: User): Promise<void> {
  try {
    const docRef = doc(db, PAYABLES_COLLECTION, id);
    const payableDoc = await getDoc(docRef);

    if (!payableDoc.exists()) {
      throw new Error('Payable not found');
    }

    const existingData = payableDoc.data() as Payable;
    const activityRecord = createDetailedActivityRecord('updated', existingData, data, firestoreUser);

    const { activityRecords, ...updateData } = data;
    const cleanData = removeUndefined(updateData);
    
    const updatePayload = {
      ...cleanData,
      activityRecords: arrayUnion(activityRecord)
    };

    await updateDoc(docRef, updatePayload);
  } catch (error) {
    console.error('Error updating payable:', error);
    throw new Error('Failed to update payable');
  }
}

export async function deletePayable(id: string, firestoreUser: User): Promise<void> {
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const payableRef = doc(db, PAYABLES_COLLECTION, id);
    const payableDoc = await getDoc(payableRef);

    if (!payableDoc.exists()) {
      throw new Error('Payable not found');
    }

    const payableData = payableDoc.data();
    if (payableData.workspace.id !== firestoreUser.workspace.id) {
      throw new Error('Payable not found in current workspace');
    }

    await deleteDoc(payableRef);
  } catch (error) {
    console.error('Error deleting payable:', error);
    throw new Error('Failed to delete payable');
  }
}

// Receivables API
export async function getReceivables({
  searchTerm,
  pageSize,
  page = 1,
  lastDoc,
  firestoreUser,
  status,
  type
}: FinanceQueryParams): Promise<{ receivables: Receivable[]; lastDoc?: QueryDocumentSnapshot; total: number }> {
  // Debug: Log user workspace info
  console.log('🔍 Debug - getReceivables called with:', {
    firestoreUser: firestoreUser ? {
      id: firestoreUser.id,
      email: firestoreUser.email,
      workspace: firestoreUser.workspace
    } : null,
    isDemo: isDemoWorkspace(firestoreUser)
  });

  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    console.log('DEMO MODE: Fetching mock receivables with filters', { searchTerm, status, type, page, pageSize });
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate delay
    
    // Load language-specific mock data
    const mockReceivables = await loadMockData<Receivable[]>('receivables', firestoreUser.language);
    console.log('🔍 Debug - Mock receivables loaded:', mockReceivables.length);
    
    const filteredData = filterMockData(mockReceivables, searchTerm, status);
    const paginatedData = paginateMockData(filteredData, pageSize, page);
    
    return {
      receivables: paginatedData.data,
      lastDoc: undefined,
      total: paginatedData.total
    };
  }

  console.log('API Call: getReceivables', { searchTerm, pageSize, lastDoc, firestoreUser, status, type });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }
  
    const receivablesRef = collection(db, RECEIVABLES_COLLECTION);
    const constraints = [where('workspace.id', '==', firestoreUser.workspace.id)];
    
    if (searchTerm) {
      constraints.push(
        where('name', '>=', searchTerm),
        where('name', '<=', searchTerm + '\uf8ff')
      );
    }

    if (status) {
      constraints.push(where('status', '==', status));
    }

    if (type) {
      constraints.push(where('type', '==', type));
    }
    
    let q = query(receivablesRef, ...constraints);
    
    if (pageSize) {
      q = query(q, limit(pageSize));
    }
    
    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }
    
    const snapshot = await getDocs(q);
    const receivables = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Receivable));
    
    return {
      receivables,
      lastDoc: snapshot.docs[snapshot.docs.length - 1],
      total: receivables.length
    };
  } catch (error) {
    console.error('Error fetching receivables:', error);
    throw new Error('Failed to fetch receivables');
  }
}

export async function getReceivableById(id: string, firestoreUser: User): Promise<Receivable | null> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    console.log(`DEMO MODE: Fetching mock receivable with id: ${id}`);
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate delay
    
    // Load language-specific mock data
    const mockReceivables = await loadMockData<Receivable[]>('receivables');
    const receivable = mockReceivables.find(r => r.id === id) || null;
    return receivable;
  }

  console.log('API Call: getReceivableById', { id, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const receivableRef = doc(db, RECEIVABLES_COLLECTION, id);
    const receivableDoc = await getDoc(receivableRef);
    
    if (!receivableDoc.exists()) {
      return null;
    }

    const receivableData = receivableDoc.data();
    
    if (receivableData.workspace.id !== firestoreUser.workspace.id) {
      return null;
    }

    return { ...receivableData, id: receivableDoc.id } as Receivable;
  } catch (error) {
    console.error('Error fetching receivable:', error);
    throw new Error('Failed to fetch receivable');
  }
}

export async function addReceivable(receivable: Omit<Receivable, 'id' | 'activityRecords' | 'workspace'>, firestoreUser: User): Promise<string> {
  console.log('API Call: addReceivable', { receivable, firestoreUser });
  
  try {
    const receivableData = addWorkspaceAndActivity(receivable, firestoreUser, 'created');
    const cleanedReceivable = removeUndefined(receivableData);
    
    const receivableRef = doc(collection(db, RECEIVABLES_COLLECTION));
    await setDoc(receivableRef, cleanedReceivable);
    return receivableRef.id;
  } catch (error) {
    console.error('Error adding receivable:', error);
    throw new Error('Failed to add receivable');
  }
}

export async function updateReceivable(id: string, data: Partial<Receivable>, firestoreUser: User): Promise<void> {
  console.log('API Call: updateReceivable', { id, data, firestoreUser });
  
  try {
    const docRef = doc(db, RECEIVABLES_COLLECTION, id);
    const receivableDoc = await getDoc(docRef);

    if (!receivableDoc.exists()) {
      throw new Error('Receivable not found');
    }

    const existingData = receivableDoc.data() as Receivable;
    const activityRecord = createDetailedActivityRecord('updated', existingData, data, firestoreUser);

    const { activityRecords, ...updateData } = data;
    const cleanData = removeUndefined(updateData);
    
    const updatePayload = {
      ...cleanData,
      activityRecords: arrayUnion(activityRecord)
    };

    await updateDoc(docRef, updatePayload);
  } catch (error) {
    console.error('Error updating receivable:', error);
    throw new Error('Failed to update receivable');
  }
}

export async function deleteReceivable(id: string, firestoreUser: User): Promise<void> {
  console.log('API Call: deleteReceivable', { id, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const receivableRef = doc(db, RECEIVABLES_COLLECTION, id);
    const receivableDoc = await getDoc(receivableRef);

    if (!receivableDoc.exists()) {
      throw new Error('Receivable not found');
    }

    const receivableData = receivableDoc.data();
    if (receivableData.workspace.id !== firestoreUser.workspace.id) {
      throw new Error('Receivable not found in current workspace');
    }

    await deleteDoc(receivableRef);
  } catch (error) {
    console.error('Error deleting receivable:', error);
    throw new Error('Failed to delete receivable');
  }
}

// Receipts API
export async function getReceipts({
  searchTerm,
  pageSize,
  lastDoc,
  firestoreUser,
  status,
  type
}: FinanceQueryParams): Promise<{ receipts: Receipt[]; lastDoc?: QueryDocumentSnapshot }> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    console.log('DEMO MODE: Fetching mock receipts with filters', { searchTerm, status, type, pageSize });
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate delay
    
    // Load language-specific mock data
    const mockReceipts = await loadMockData<Receipt[]>('receipts');
    const filteredData = filterMockData(mockReceipts, searchTerm, status);
    const paginatedData = paginateMockData(filteredData, pageSize, 1);
    
    return {
      receipts: paginatedData.data,
      lastDoc: undefined
    };
  }

  console.log('API Call: getReceipts', { searchTerm, pageSize, lastDoc, firestoreUser, status, type });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }
  
    const receiptsRef = collection(db, RECEIPTS_COLLECTION);
    const constraints = [where('workspace.id', '==', firestoreUser.workspace.id)];
    
    if (searchTerm) {
      constraints.push(
        where('name', '>=', searchTerm),
        where('name', '<=', searchTerm + '\uf8ff')
      );
    }

    if (status) {
      constraints.push(where('status', '==', status));
    }

    if (type) {
      constraints.push(where('type', '==', type));
    }
    
    let q = query(receiptsRef, ...constraints);
    
    if (pageSize) {
      q = query(q, limit(pageSize));
    }
    
    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }
    
    const snapshot = await getDocs(q);
    const receipts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Receipt));
    
    return {
      receipts,
      lastDoc: snapshot.docs[snapshot.docs.length - 1]
    };
  } catch (error) {
    console.error('Error fetching receipts:', error);
    throw new Error('Failed to fetch receipts');
  }
}

export async function getReceiptById(id: string, firestoreUser: User): Promise<Receipt | null> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    console.log(`DEMO MODE: Fetching mock receipt with id: ${id}`);
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate delay
    
    // Load language-specific mock data
    const mockReceipts = await loadMockData<Receipt[]>('receipts');
    const receipt = mockReceipts.find(r => r.id === id) || null;
    return receipt;
  }

  console.log('API Call: getReceiptById', { id, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const receiptRef = doc(db, RECEIPTS_COLLECTION, id);
    const receiptDoc = await getDoc(receiptRef);
    
    if (!receiptDoc.exists()) {
      return null;
    }

    const receiptData = receiptDoc.data();
    
    if (receiptData.workspace.id !== firestoreUser.workspace.id) {
      return null;
    }

    return { ...receiptData, id: receiptDoc.id } as Receipt;
  } catch (error) {
    console.error('Error fetching receipt:', error);
    throw new Error('Failed to fetch receipt');
  }
}

export async function addReceipt(receipt: Omit<Receipt, 'id'>, firestoreUser: User): Promise<string> {
  console.log('API Call: addReceipt', { receipt, firestoreUser });
  
  try {
    const receiptData = addWorkspaceAndActivity(receipt, firestoreUser, 'created');
    const cleanedReceipt = removeUndefined(receiptData);
    
    const receiptRef = doc(collection(db, RECEIPTS_COLLECTION));
    await setDoc(receiptRef, cleanedReceipt);
    return receiptRef.id;
  } catch (error) {
    console.error('Error adding receipt:', error);
    throw new Error('Failed to add receipt');
  }
}

export async function updateReceipt(id: string, data: Partial<Receipt>, firestoreUser: User): Promise<void> {
  console.log('API Call: updateReceipt', { id, data, firestoreUser });
  
  try {
    const docRef = doc(db, RECEIPTS_COLLECTION, id);
    const receiptDoc = await getDoc(docRef);

    if (!receiptDoc.exists()) {
      throw new Error('Receipt not found');
    }

    const existingData = receiptDoc.data() as Receipt;
    const activityRecord = createDetailedActivityRecord('updated', existingData, data, firestoreUser);

    const cleanData = removeUndefined(data);
    
    const updatePayload = {
      ...cleanData,
      activityRecords: arrayUnion(activityRecord)
    };

    await updateDoc(docRef, updatePayload);
  } catch (error) {
    console.error('Error updating receipt:', error);
    throw new Error('Failed to update receipt');
  }
}

export async function deleteReceipt(id: string, firestoreUser: User): Promise<void> {
  console.log('API Call: deleteReceipt', { id, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const receiptRef = doc(db, RECEIPTS_COLLECTION, id);
    const receiptDoc = await getDoc(receiptRef);

    if (!receiptDoc.exists()) {
      throw new Error('Receipt not found');
    }

    const receiptData = receiptDoc.data();
    if (receiptData.workspace.id !== firestoreUser.workspace.id) {
      throw new Error('Receipt not found in current workspace');
    }

    await deleteDoc(receiptRef);
  } catch (error) {
    console.error('Error deleting receipt:', error);
    throw new Error('Failed to delete receipt');
  }
}

// Accounting API - Get payables and receivables with completed receipts
export async function getPayablesAndReceivables({
  searchTerm,
  pageSize,
  page = 1,
  lastDoc,
  firestoreUser,
  status,
  type
}: FinanceQueryParams): Promise<{ records: Array<Payable | Receivable>; lastDoc?: QueryDocumentSnapshot; total: number }> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    console.log('DEMO MODE: Fetching mock payables and receivables with filters', { searchTerm, status, type, page, pageSize });
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate delay
    
    // Load language-specific mock data
    const mockPayables = await loadMockData<Payable[]>('payables', firestoreUser.language);
    const mockReceivables = await loadMockData<Receivable[]>('receivables');
    
    // Filter payables and receivables with completed receipts
    const filteredPayables = mockPayables.filter(payable => 
      payable.receipts?.some((r: Receipt) => r.status === 'completed')
    );
    
    const filteredReceivables = mockReceivables.filter(receivable => 
      receivable.receipts?.some((r: Receipt) => r.status === 'completed')
    );

    const allRecords = [...filteredPayables, ...filteredReceivables];
    
    // Apply search filter if provided
    let filteredRecords = allRecords;
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLowerCase();
      filteredRecords = allRecords.filter(record => 
        record.name.toLowerCase().includes(lowercasedSearch) ||
        (record as any).vendor?.name.toLowerCase().includes(lowercasedSearch) ||
        (record as any).resident?.name.toLowerCase().includes(lowercasedSearch)
      );
    }

    // Apply pagination
    const paginatedData = paginateMockData(filteredRecords, pageSize, page);
    
    return {
      records: paginatedData.data,
      lastDoc: undefined,
      total: paginatedData.total
    };
  }

  console.log('API Call: getPayablesAndReceivables', { searchTerm, pageSize, lastDoc, firestoreUser, status, type });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    // Get all payables for the workspace
    const payablesRef = collection(db, PAYABLES_COLLECTION);
    const payablesQuery = query(
      payablesRef,
      where('workspace.id', '==', firestoreUser.workspace.id)
    );
    const payablesSnapshot = await getDocs(payablesQuery);
    const payables = payablesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Payable));

    // Get all receivables for the workspace
    const receivablesRef = collection(db, RECEIVABLES_COLLECTION);
    const receivablesQuery = query(
      receivablesRef,
      where('workspace.id', '==', firestoreUser.workspace.id)
    );
    const receivablesSnapshot = await getDocs(receivablesQuery);
    const receivables = receivablesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Receivable));

    // Filter to only include records with completed receipts
    const filteredPayables = payables.filter(payable => 
      payable.receipts?.some((r: Receipt) => r.status === 'completed')
    );
    
    const filteredReceivables = receivables.filter(receivable => 
      receivable.receipts?.some((r: Receipt) => r.status === 'completed')
    );

    const allRecords = [...filteredPayables, ...filteredReceivables];

    return {
      records: allRecords,
      lastDoc: undefined, // For now, not implementing pagination
      total: allRecords.length
    };
  } catch (error) {
    console.error('Error fetching accounting records:', error);
    throw new Error('Failed to fetch accounting records');
  }
}

// Bulk receivable creation
export async function createBulkReceivables(
  receivables: Omit<Receivable, 'id' | 'activityRecords' | 'workspace'>[],
  firestoreUser: User
): Promise<{ successCount: number; errorCount: number; errors: string[] }> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    // Simulate processing delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // In demo mode, just return success
    return {
      successCount: receivables.length,
      errorCount: 0,
      errors: []
    };
  }

  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const receivablesRef = collection(db, RECEIVABLES_COLLECTION);
    const errors: string[] = [];
    let successCount = 0;

    // Process receivables in batches to avoid Firestore limits
    const BATCH_SIZE = 500;
    const batches = [];
    
    for (let i = 0; i < receivables.length; i += BATCH_SIZE) {
      batches.push(receivables.slice(i, i + BATCH_SIZE));
    }

    for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
      const batch = batches[batchIndex];
      
      try {
        // Use Promise.all for parallel processing within each batch
        const batchPromises = batch.map(async (receivable) => {
          const docRef = doc(receivablesRef);
          const receivableData = {
            ...receivable,
            workspace: {
              id: firestoreUser.workspace!.id,
              name: firestoreUser.workspace!.name
            },
            activityRecords: [
              createDetailedActivityRecord(
                'created',
                null,
                receivable,
                firestoreUser
              )
            ]
          };

          await setDoc(docRef, removeUndefined(receivableData));
          return docRef.id;
        });

        await Promise.all(batchPromises);
        successCount += batch.length;
      } catch (error) {
        console.error(`Error creating receivables batch ${batchIndex + 1}:`, error);
        errors.push(`Batch ${batchIndex + 1}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    return {
      successCount,
      errorCount: receivables.length - successCount,
      errors
    };
  } catch (error) {
    console.error('Error in bulk receivable creation:', error);
    throw new Error('Failed to create receivables in bulk');
  }
} 